import { useCombobox } from 'downshift';
import { useState, Fragment, useEffect, useRef } from 'react';

import { CloseOutlineIc, SearchOutlineIc } from '@dsch/dd-icons';

import {
  InputButton,
  InputButtonProps,
} from '../../../../components/Toolkit/InputButton/InputButton';
import { Text } from '../../../../components/ToolkitV2/Text/Text';

import {
  ListItem,
  InputWrapper,
  SearchIcon,
  KeywordSearchInput,
  ClearButton,
  Heading,
  List,
  EmptyList,
  ErrorList,
  ErrorListContent,
} from './KeywordSearch.styled';

import {
  SearchableDropdown,
  highlight,
} from '../../../../components/ToolkitV2/SearchableDropdown/SearchableDropdown';

import type { TextProps } from '../../../../components/ToolkitV2/Text/Text.typed';
import type { MakeModelOption } from '../../MakeModelKeywordSearch.typed';
import { DisplayContainerConfig } from '../../../../components/Support/DisplayContainer/DisplayContainer';
import { MakeModelKeywordSearchModal } from '../MakeModelKeywordSearchModal/MakeModelKeywordSearchModal';
import { theme } from '../../../../helpers/Theme';
import ResponsiveDisplay from '../ResponsiveDisplay/ResponsiveDisplay';
import { useMakeModelKeywordSearch } from './KeywordSearch.hook';
import Link from 'next/link';
import { ErrorIcon } from '../../../../components/Toolkit/Notification/Notification.styled';
import { LinkButton } from '../../../../components/Toolkit/Button/LinkButton';

type DownshiftKeywordSearchProps = {
  value: string;
  onSelect: (option: MakeModelOption) => void;
  initialItems: Array<MakeModelOption>;
  modalDisplayConfig: DisplayContainerConfig;
  dropdownDisplayConfig: DisplayContainerConfig;
  minPrice?: string;
  maxPrice?: string;
  minYear?: string;
  maxYear?: string;
};

/**
 * This component is responsible for displaying the UI and managing its logic in an accessible manner.
 *
 * It contains a modal and dropdown. Each of these can be configured to be displayed or hidden on different screen sizes.
 * They both contain list items that display makes or make and model options.
 *
 * useCombobox from downshift is used to managed the internal state of the dropdown and list items for both and the modal and the dropdown.
 * As a result, the input of the component is consistent across screen sizes.
 *
 * The state of the modal is managed separately. React modal requires its own state for displaying the modal with the list and dismissing it.
 */
export const KeywordSearch = (props: DownshiftKeywordSearchProps) => {
  const {
    value,
    onSelect,
    initialItems,
    modalDisplayConfig,
    dropdownDisplayConfig,
    minPrice,
    maxPrice,
    minYear,
    maxYear,
  } = props;

  const [inputValue, setInputValue] = useState<string>('');
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const inputRef = useRef<HTMLInputElement>(null);
  // Track when user is interacting with list items to prevent blur from closing menu
  const isInteractingWithMenuRef = useRef(false);

  const {
    data: makeModelOptions,
    isPending,
    isError,
    reload,
  } = useMakeModelKeywordSearch({
    minPrice,
    maxPrice,
    minYear,
    maxYear,
    keyword: inputValue,
    listIsOpen: isOpen,
  });

  // We need the items to be in a state because we want to display the last items
  // while the new items are being fetched
  const [items, setItems] = useState<MakeModelOption[] | undefined>(undefined);
  useEffect(() => {
    if (inputValue === '') {
      setItems(initialItems);
    } else if (makeModelOptions !== undefined) {
      setItems(makeModelOptions);
    }
  }, [makeModelOptions, initialItems, inputValue]);

  const {
    getInputProps,
    getMenuProps,
    getToggleButtonProps,
    getItemProps,
    highlightedIndex,
    getLabelProps,
    openMenu,
    closeMenu,
  } = useCombobox({
    items: items ?? [],
    itemToString: (item) => item?.label ?? '',
    initialInputValue: value,
    onSelectedItemChange: ({ selectedItem }) => {
      onSelect(selectedItem);
    },
    inputValue,
    onInputValueChange: ({ inputValue }) => setInputValue(inputValue),
    stateReducer(_state, actionAndChanges) {
      const { type, changes } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputBlur:
          // Prevent blur from closing the menu if user is interacting with list items.
          // This fixes an issue where clicking a list item triggers blur before click,
          // especially on touch devices or when modal focus isn't properly tracked.
          if (isInteractingWithMenuRef.current) {
            return { ...changes, isOpen: true };
          }
          return changes;
        case useCombobox.stateChangeTypes.InputClick:
          // Without this, the dropdown would close when the searchbox is selected.
          return {
            ...changes,
            isOpen: true,
          };
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
          // Reset interaction flag after item is clicked
          isInteractingWithMenuRef.current = false;
          return {
            ...changes,
            // Don't close the dropdown when we select an item from the initial list of makes
            isOpen: items === initialItems,
            // Avoid having (All Models) in the input when an item is selected
            inputValue: `${changes.selectedItem?.value?.make} ${
              changes.selectedItem?.value?.model ?? ''
            }`.trim(),
          };
        default:
          return changes;
      }
    },
    isOpen,
    onIsOpenChange: ({ isOpen }) => {
      setIsOpen(isOpen);
      if (isOpen) {
        setInputValue(value);
      }
    },
  });

  const TEXT_PROPS: TextProps = {
    size: 'M16',
    forwardedAs: 'span',
  };

  const KEYWORD_SEARCH_INPUT_PROPS = {
    type: 'search',
    placeholder: 'Start typing a make or model',
    'aria-label': 'Keyword search input',
  };

  const INPUT_BUTTON_PROPS: InputButtonProps = {
    placeholder: 'All makes/models',
    isActive: false,
    heightVariant: 'LARGE',
  };

  const clearSelection = (
    e:
      | React.MouseEvent<HTMLButtonElement>
      | React.TouchEvent<HTMLButtonElement>,
  ) => {
    e.preventDefault();
    setInputValue('');
    onSelect({ label: '', value: null, count: null });
  };

  const renderListItems = () => {
    if (isError && !isPending) {
      return (
        <ErrorList>
          <ErrorListContent>
            <ErrorIcon
              style={{ marginBottom: 'auto' }}
              color={theme.donedeal.colors.GREY}
              width={40}
              height={40}
            />
            <Text size="L18" color="GREY_DARKER">
              <strong>Something went wrong</strong>
            </Text>
            <Text {...TEXT_PROPS} align="center" color="GREY_DARK">
              There was a problem processing your request -- please click{' '}
              <LinkButton inline onClick={reload}>
                reload
              </LinkButton>{' '}
              to try again.
            </Text>
          </ErrorListContent>
        </ErrorList>
      );
    }

    if (items === undefined) {
      return;
    }

    if (items.length === 0) {
      return (
        <EmptyList>
          <div>
            <Text {...TEXT_PROPS} color="GREY_DARKER">
              We couldn't find matches for "<strong>{inputValue}</strong>"
            </Text>
          </div>
          <div>
            <Link href="/cars">View all Makes & Models</Link>
          </div>
        </EmptyList>
      );
    }

    const handleMenuItemInteractionStart = () => {
      isInteractingWithMenuRef.current = true;
    };

    return items.map((item, index) => {
      const { label, count, groupLabel } = item;
      return (
        <Fragment key={`${label}-${index}`}>
          {groupLabel && (
            <Heading weight="bold" color="GREY_DARKER" size="M16">
              {groupLabel}
            </Heading>
          )}
          <ListItem
            {...getItemProps({
              item,
              index,
            })}
            onMouseDown={handleMenuItemInteractionStart}
            onTouchStart={handleMenuItemInteractionStart}
            highlighted={highlightedIndex === index}
          >
            {
              <Text {...TEXT_PROPS} color="GREY_DARKER">
                {highlight({
                  item: label,
                  searchText: inputValue,
                })}
              </Text>
            }
            {count ? (
              <Text {...TEXT_PROPS} color="GREY_DARK">
                {' '}
                ({count})
              </Text>
            ) : (
              ''
            )}
          </ListItem>
        </Fragment>
      );
    });
  };

  return (
    <>
      {/* Modal
        NB: We're using ResponsiveDisplay instead of DisplayContainer to make sure
        we don't have the two components mounted on the DOM at the same time.
        Indeed if that is the case we would have problems with refs because
        we're using the same downshift props for both. */}
      <ResponsiveDisplay
        small={modalDisplayConfig.small === 'block'}
        medium={modalDisplayConfig.medium === 'block'}
        large={modalDisplayConfig.large === 'block'}
      >
        <InputButton
          onClick={openMenu}
          value={value}
          aria-label="Open make & model modal"
          {...INPUT_BUTTON_PROPS}
        />
        <MakeModelKeywordSearchModal isOpen={isOpen} onRequestClose={closeMenu}>
          <label {...getLabelProps()}>
            <InputWrapper
              margin={`${theme.donedeal.spacing.S8} ${theme.donedeal.spacing.M16} 0`}
            >
              <SearchIcon aria-label="Search">
                <SearchOutlineIc />
              </SearchIcon>
              <KeywordSearchInput
                {...KEYWORD_SEARCH_INPUT_PROPS}
                {...getInputProps({ ref: inputRef, autoFocus: true })}
              />
              {inputValue && (
                <ClearButton
                  // Use onMouseDown or onTouchStart to prevent the dropdown from closing
                  // Indeed, preventDefault prevents the input from losing focus
                  onMouseDown={clearSelection}
                  onTouchStart={clearSelection}
                  aria-label="Clear search"
                  ofType="GHOST"
                >
                  <CloseOutlineIc width={16} height={16} />
                </ClearButton>
              )}
            </InputWrapper>
          </label>
          <List {...getMenuProps()}>{isOpen && renderListItems()}</List>
        </MakeModelKeywordSearchModal>
      </ResponsiveDisplay>

      {/* Dropdown */}
      <ResponsiveDisplay
        small={dropdownDisplayConfig.small === 'block'}
        medium={dropdownDisplayConfig.medium === 'block'}
        large={dropdownDisplayConfig.large === 'block'}
      >
        {!isOpen && (
          <InputButton
            value={value}
            aria-label="Toggle make & model dropdown"
            {...INPUT_BUTTON_PROPS}
            {...getToggleButtonProps({ tabIndex: 0 })}
          />
        )}
        <SearchableDropdown isOpen={isOpen}>
          <label {...getLabelProps()}>
            <InputWrapper>
              <SearchIcon aria-label="Search">
                <SearchOutlineIc />
              </SearchIcon>
              <KeywordSearchInput
                {...KEYWORD_SEARCH_INPUT_PROPS}
                {...getInputProps()}
              />
              {inputValue && (
                <ClearButton
                  // Use onMouseDown or onTouchStart to prevent the dropdown from closing
                  // Indeed, preventDefault prevents the input from losing focus
                  onMouseDown={clearSelection}
                  onTouchStart={clearSelection}
                  aria-label="Clear search"
                  ofType="GHOST"
                >
                  <CloseOutlineIc width={16} height={16} />
                </ClearButton>
              )}
            </InputWrapper>
          </label>
          <List {...getMenuProps()} variant="dropdown">
            {isOpen && renderListItems()}
          </List>
        </SearchableDropdown>
      </ResponsiveDisplay>
    </>
  );
};
